import { useEffect, useRef, useState, type FC } from 'react';
import { WEBSITE_URL } from '../config';
import {
  ANALYTICS_SETTINGS_EVENT,
  readAnalyticsConsent,
  setAnalyticsConsent,
  type AnalyticsConsent,
} from '../analyticsConsent';
import { copperFill } from '../account/accountUi';

/**
 * The dashboard's analytics choice, same copy and behaviour as the website
 * card (apps/website/src/analytics-consent.js): shown until the user chooses,
 * reopened by the footer's "Analytics settings", a short confirmation after a
 * choice. Surface, border and buttons follow the account menu (bg-[#222],
 * border-white/10) and CodeBlock's copper Copy button. Sits below modals
 * (Modal.tsx z-[60]).
 */
const CONFIRM_MS = 2800;

type View = { mode: 'prompt'; current: AnalyticsConsent | null } | { mode: AnalyticsConsent };

export const COPY = {
  ask: 'We would like to measure which pages people read, using an analytics service that stores an identifier in your browser. It is off unless you say yes. ',
  policy: 'How we handle data',
  current: (c: AnalyticsConsent) =>
    c === 'granted' ? 'Analytics is on in this browser. ' : 'Analytics is off in this browser. ',
  onLead: 'Analytics on.',
  onTail: ' We will measure page reads in this browser.',
  offLead: 'Analytics off.',
  offTail: ' Nothing is measured. We will not ask again on this browser.',
} as const;

const btn =
  'min-h-[44px] min-w-[6.5rem] flex-1 rounded-lg px-4 py-2 text-sm font-medium transition sm:flex-none';

export const AnalyticsConsentCard: FC = () => {
  const [view, setView] = useState<View | null>(() =>
    readAnalyticsConsent() === null ? { mode: 'prompt', current: null } : null
  );
  const timer = useRef<number | null>(null);

  const stopTimer = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };

  useEffect(() => {
    const reopen = () => {
      stopTimer();
      setView({ mode: 'prompt', current: readAnalyticsConsent() });
    };
    window.addEventListener(ANALYTICS_SETTINGS_EVENT, reopen);
    return () => {
      window.removeEventListener(ANALYTICS_SETTINGS_EVENT, reopen);
      stopTimer();
    };
  }, []);

  if (!view) return null;

  const close = () => {
    stopTimer();
    setView(null);
  };

  const decide = (value: AnalyticsConsent) => {
    setAnalyticsConsent(value);
    setView({ mode: value });
    stopTimer();
    timer.current = window.setTimeout(close, CONFIRM_MS);
  };

  const dismissable = view.mode !== 'prompt' || view.current !== null;

  return (
    <div
      data-testid="analytics-consent"
      data-state={
        view.mode === 'prompt' ? 'prompt' : view.mode === 'granted' ? 'accepted' : 'declined'
      }
      role={view.mode === 'prompt' ? 'dialog' : 'status'}
      aria-live="polite"
      aria-label={
        view.mode === 'prompt'
          ? 'Analytics consent'
          : view.mode === 'granted'
            ? 'Analytics accepted'
            : 'Analytics declined'
      }
      className="fixed inset-x-3 bottom-3 z-40 mx-auto flex max-w-xl flex-col items-center gap-3 rounded-lg border border-white/10 bg-[#222] px-5 py-4 text-center text-sm leading-relaxed text-gray-300 shadow-xl"
    >
      {view.mode === 'prompt' ? (
        <>
          <p className={dismissable ? 'px-7' : undefined}>
            {view.current && (
              <span className="font-semibold text-[#e6a98c]">{COPY.current(view.current)}</span>
            )}
            {COPY.ask}
            <a
              href={`${WEBSITE_URL}/legal/privacy-policy.html#cookies`}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap text-[#e6a98c] underline underline-offset-2"
            >
              {COPY.policy}
            </a>
            .
          </p>
          <div className="flex w-full justify-center gap-2">
            <button
              type="button"
              onClick={() => decide('denied')}
              className={`${btn} border border-white/20 text-gray-200 hover:bg-white/5`}
            >
              Decline
            </button>
            <button
              type="button"
              onClick={() => decide('granted')}
              className={`${btn} ${copperFill}`}
            >
              Accept
            </button>
          </div>
        </>
      ) : (
        <p className="px-7">
          <span className="font-semibold text-[#e6a98c]">
            {view.mode === 'granted' ? COPY.onLead : COPY.offLead}
          </span>
          {view.mode === 'granted' ? COPY.onTail : COPY.offTail}
        </p>
      )}
      {dismissable && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={close}
          className="absolute right-2 top-1.5 rounded-md px-2 text-xl leading-none text-gray-400 transition hover:bg-white/5 hover:text-white"
        >
          ×
        </button>
      )}
    </div>
  );
};
